import { Badge } from 'react-bootstrap';
import type { SkinAnalysisPredictValidationResponse } from '@/models/SkinAnalysisPredictValidation';
import { ValidationReportSection } from '@/pages/recommendation-lab/ValidationReportSection';
import { ValidationWeatherGuidance } from '@/pages/recommendation-lab/ValidationWeatherGuidance';
import {
  ValidationConcernMatches,
  ValidationMilestones,
  ValidationRoutineCards,
  ValidationRoutineReveal,
} from '@/pages/recommendation-lab/ValidationRoutineSection';

interface PredictValidationResultPanelProps {
  result: SkinAnalysisPredictValidationResponse;
}

/**
 * Customer-result preview in Figma order.
 * Only the copy is under review here, so the raw planner funnel stays out of this panel.
 */
export function PredictValidationResultPanel({ result }: PredictValidationResultPanelProps) {
  const { environment, report, routineReveal, concernMatches, routineCards, expectedChanges } = result.clientResult;
  const promptTrace = result.clientResult.promptTrace ?? [];
  return (
    <section className="client-routine-preview">
      <ValidationWeatherGuidance guidanceText={environment.guidanceText} />
      <ValidationReportSection report={report} />
      <ValidationRoutineReveal reveal={routineReveal} />
      <ValidationConcernMatches concernMatches={concernMatches} />
      <ValidationRoutineCards routineCards={routineCards} />
      <ValidationMilestones expectedChanges={expectedChanges} />
      <footer className="client-prompt-trace">
        <details>
          <summary>
            Prompt config {promptTrace[0]?.configFingerprint?.slice(0, 12) ?? 'unavailable'} · {promptTrace.length} keys
          </summary>
          {promptTrace.map((trace) => (
            <span
              key={trace.key}
              title={
                trace.templateFingerprint || trace.systemTemplateFingerprint
                  ? `system ${trace.systemTemplateFingerprint ?? 'unavailable'} · user ${trace.templateFingerprint ?? 'unavailable'}`
                  : undefined
              }
            >
              {trace.key.replace(/_/g, ' ')}
              <Badge
                bg={trace.systemSource === 'redis' ? 'warning' : 'secondary'}
                text={trace.systemSource === 'redis' ? 'dark' : 'light'}
              >
                SYS {trace.systemSource === 'redis' ? 'REDIS' : trace.systemSource === 'code_default' ? 'CODE' : 'N/A'}
              </Badge>
              <Badge
                bg={trace.source === 'redis' ? 'warning' : trace.source === 'legacy_v1' ? 'info' : 'secondary'}
                text={trace.source === 'redis' ? 'dark' : 'light'}
              >
                USER {trace.source === 'redis' ? 'REDIS' : trace.source === 'legacy_v1' ? 'LEGACY V1' : 'CODE'}
              </Badge>
            </span>
          ))}
        </details>
      </footer>
    </section>
  );
}
